/* React hooks */
import { useEffect, useState } from "react";
/* Components*/
import FormInputs from "../FormInputs";
import { getDocumentTypeList, uploadDocument } from "../../api/admin/documentReq";
import useAuth from "../../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import useData from "../../hooks/useData";
import {
    fileToBase64,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput
} from "../../helper/tools";
import FormButtons from "../../components/common/FormButtons";
import Icon from "../../components/common/Icon";


export default function FormDocuments({ legend, setShowModal, apiDocumentInfos }) {
    const { id } = useParams();
    const [base64String, setBase64String] = useState(null);
    const { auth } = useAuth();
    const { documentTypeList, setDocumentTypeList } = useData();
    const token = auth.token;
    const navigate = useNavigate();
    const { documentInfos } = useData();
    const [values, setValues] = useState();

    const onChange = (e) => {
        setValues({ ...values, [e.target.name]: e.target.value })
        validateFormInput(e)
    };

    useEffect(() => {
        setValues(documentInfos)
    }, [documentInfos]);

    // Un document existant peut avoir une valeur de "type" qui ne correspond plus à aucun choix
    // valide (ex: liste des types restreinte depuis l'enregistrement du document). Le <select>
    // affichait alors silencieusement le premier type de la liste sans que values.type soit mis à
    // jour en conséquence : à l'enregistrement, c'est l'ancienne valeur invalide qui repartait et
    // était rejetée par le serveur, sans que l'utilisateur ait pu s'en rendre compte. On aligne
    // donc values.type sur ce qui est effectivement affiché dès que la liste des types est connue.
    useEffect(() => {
        if (!documentTypeList?.length || values?.type === undefined) {
            return;
        }
        const isValidType = documentTypeList.some(type => String(type?.id) === String(values?.type));
        if (!isValidType) {
            setValues(prev => ({ ...prev, type: documentTypeList[0]?.id }));
        }
    }, [documentTypeList, values?.type]);

    useEffect(() => {
        apiDocumentTypeList();
    }, [token]);

    useEffect(() => {
        apiDocumentInfos();
    }, [id]);

    const handleHtmlContent = (e, inputName) => {
        setValues({ ...values, [inputName]: e })
    };

    const apiDocumentTypeList = async () => {
        const response = await getDocumentTypeList(token);
        setDocumentTypeList(response);
    }

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            try {
                const base64StringConst = await fileToBase64(selectedFile);
                setBase64String(base64StringConst);
                setValues({ ...values, [e.target.name]: selectedFile.name });
            } catch (error) {
                console.error("Erreur lors de la conversion en base64 : ", error);
            }
        }
    };

    const handleRemoveModal = () => {
        if (id === "0") {
            navigate("/admin/documents")
        }
        setShowModal(false)
    };

    const optionsDocumentTypeList = documentTypeList?.map((type) => (
        { [type?.id]: type?.name }
    ));

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const requestBody = {
                id: parseInt(id),
                name: values?.name || '',
                description: values?.description || '',
                url: values?.url || '',
                type: parseInt(values?.type) || 0,
                fileName: values?.fileName || '',
            };

            if (base64String) {
                requestBody.file = {
                    content: base64String,
                    name: values?.name || values?.fileName,
                };
            }

            const response = await uploadDocument(token, requestBody, id);

            removeErrorList()

            if (!response?.response?.data?.error) {
                if (id === "0") {
                    navigate("/admin/documents")
                }
                setShowModal(false)
                apiDocumentInfos()
            } else {
                getErrorListFromValidation(response?.response?.data?.error)
            }
        } catch (error) {
            console.error("Erreur lors de la soumission du formulaire : ", error);
            getErrorListFromValidation(error?.response?.data?.error)
        }
    }

    const inputs = [
        {
            id: 1,
            name: "name",
            type: "text",
            placeholder: "Nom du document",
            label: "Nom du document",
            required: true,
            pattern: patternListChoices?.name,
        },
        {
            id: 3,
            name: "description",
            type: "html",
            placeholder: "Description",
            label: "Description",
            content: values?.description,
            pattern: patternListChoices?.name,
        },
        {
            id: 4,
            name: "fileName",
            type: "file",
            label: "Source du document (obligatoire si pas de lien)",
        },
        {
            id: 5,
            name: "url",
            type: "text",
            label: "Lien de téléchargement (obligatoire si pas de source)",
            pattern: patternListChoices?.url
        },
        {
            id: 7,
            name: "type",
            type: "select",
            label: "Type de document",
            options: optionsDocumentTypeList,
        }
    ];

    return (
        <form onSubmit={handleSubmit} method="post" noValidate>
            <fieldset>
                {(legend !== "no-legend") && <Icon source="fa fa-file-lines" span={legend}/>}
                {inputs.map(input => (
                    <FormInputs
                        type={input.type}
                        name={input.name}
                        id={input.id}
                        label={input.label}
                        placeholder={input.placeholder}
                        value={values?.[input.name]}
                        onChange={onChange}
                        onChangeHtml={(e) => handleHtmlContent(e, input.name)}
                        required={input.required}
                        options={input.options}
                        selected={input.selected}
                        checked={input.checked}
                        key={input.id + input.name}
                        handleFileChange={handleFileChange}
                        content={input.content}
                        pattern={input.pattern}
                        className={input.className}
                    />
                ))}
                <div className="errors"></div>
            </fieldset>
            <FormButtons
                handleSubmit={handleSubmit}
                handleRemoveModal={handleRemoveModal}
            />
        </form>
    )
}